// download-chrome — the "Download" button on the student's own activity working copy (Trello DNivkjIh).
//
// The FIFTEENTH sibling chrome module, NOT uniformly wired: it ALLOW-LISTS the surface to exactly
// `assignment | challenge | guided-make` (the three pages where a student edits their OWN saved
// working copy) and is a no-op without the server-rendered `#runner-download` button, which the
// templates render only on the editable branch (never on a plan-locked assignment or a locked
// guided make). Wired LATE from main.js, inside `loadProject().then()` after `surface.wire`, because
// it needs `core` (the live buffer) and the load `payload` (the asset base URL, the asset list and the
// mode) — so it is never wired on a LOCKED plan page.
//
// The zip is built ENTIRELY IN THE BROWSER, exactly like the game portfolio's "Download my game
// (.zip)": the code is `core.getFiles()` (what the student sees, unsaved edits included), each
// asset's bytes are fetched SEQUENTIALLY from the same-origin `<assetBaseUrl>/<name>` (the URL the
// runner itself loads them from) under the 25 MB cap, and `jszip` is loaded LAZILY on click (the
// pinned `jszip` chunk). It never uses `editorApi.downloadProject` / `POST /api/projects/download`:
// that route looks each asset up by its bare file name and silently drops every one. The pure rules
// live in `activity-download.js`.
//
// Live uploads: `assets-panel.js` dispatches `pc-assets-changed` on `document` after every
// successful asset save, and this module replaces its asset list from it, so a picture uploaded a
// moment ago is in the next download. No read-only check: a schedule-locked page keeps a working
// Download (it is the student's own code). editor-core.js / editor-api.js are unchanged. Never throws.

import {
  resolveMode,
  usableAssets,
  planDownload,
  downloadFileName,
  downloadNotice,
  capDecision,
  ASSET_CAP_BYTES,
  FAILED_NOTICE
} from './activity-download.js';

var ALLOWED_SURFACES = ['assignment', 'challenge', 'guided-make'];

var LABEL = 'Download';
var BUSY_LABEL = 'Preparing…';

function defaultLoadZip() {
  return import('jszip').then(function (m) { return m.default || m; });
}

/**
 * Wire the Download button. `ctx` = { core, surface, payload, doc }. `deps` is a TEST SEAM:
 * `{ fetch, loadZip, URL, setTimeout }` (each defaults to the browser's own). Returns true when wired.
 */
export function wireDownload(ctx, deps) {
  try {
    var doc = ctx && ctx.doc;
    if (!doc || !ctx.core) { return false; }
    if (ALLOWED_SURFACES.indexOf(ctx.surface) === -1) { return false; }
    var btn = doc.getElementById('runner-download');
    if (!btn) { return false; }
    var statusEl = doc.getElementById('runner-download-status');
    var d = deps || {};
    var win = doc.defaultView || (typeof window !== 'undefined' ? window : {});
    var fetchFn = d.fetch || (typeof fetch === 'function' ? fetch : null);
    var loadZip = d.loadZip || defaultLoadZip;
    var urlApi = d.URL || win.URL || (typeof URL !== 'undefined' ? URL : null);
    var later = d.setTimeout || win.setTimeout || (typeof setTimeout === 'function' ? setTimeout : null);

    var core = ctx.core;
    var payload = ctx.payload || {};
    var mode = resolveMode(payload.mode);
    var assetBaseUrl = typeof payload.assetBaseUrl === 'string' ? payload.assetBaseUrl : '';
    var assets = Array.isArray(payload.assets) ? payload.assets.slice() : [];
    var busy = false;

    doc.addEventListener('pc-assets-changed', function (e) {
      var detail = e && e.detail;
      if (!detail) { return; }
      // '' (remove-all) means zero assets; a reply with no base URL keeps the one we had.
      if (typeof detail.assetBaseUrl === 'string') { assetBaseUrl = detail.assetBaseUrl; }
      assets = Array.isArray(detail.assets) ? detail.assets.slice() : [];
    });

    function setNotice(text, isError) {
      if (!statusEl) { return; }
      statusEl.textContent = text || '';
      if (statusEl.classList) { statusEl.classList.toggle('runner-error', !!isError); }
    }

    function setBusy(on) {
      busy = on;
      if (on) {
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = BUSY_LABEL;
      } else {
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('aria-busy');
        btn.textContent = LABEL;
      }
    }

    // Sequential, each counted once toward the cap; a failed or non-OK fetch is skipped and listed.
    function fetchAssets(names) {
      var fetched = [];
      var skipped = [];
      var total = 0;
      var overCap = false;
      if (!assetBaseUrl || !fetchFn) {
        return Promise.resolve({ fetched: [], skipped: [], overCap: false });
      }
      var chain = Promise.resolve();
      names.forEach(function (name) {
        chain = chain.then(function () {
          if (overCap) { return; }
          return fetchFn(assetBaseUrl + '/' + encodeURIComponent(name), { credentials: 'same-origin' })
            .then(function (r) {
              if (!r || !r.ok) { skipped.push(name); return; }
              var declared = Number(r.headers && r.headers.get ? r.headers.get('Content-Length') : 0);
              if (declared > 0 && capDecision(total, declared, ASSET_CAP_BYTES).over) { overCap = true; return; }
              return r.arrayBuffer().then(function (bytes) {
                var decision = capDecision(total, bytes.byteLength, ASSET_CAP_BYTES);
                if (decision.over) { overCap = true; return; }
                total = decision.total;
                fetched.push({ name: name, bytes: bytes });
              });
            })
            .catch(function () { skipped.push(name); });
        });
      });
      return chain.then(function () {
        // Over the cap: discard every asset byte — a code-only zip, and say so.
        if (overCap) { return { fetched: [], skipped: [], overCap: true }; }
        return { fetched: fetched, skipped: skipped, overCap: false };
      });
    }

    function save(blob, filename) {
      var href = urlApi.createObjectURL(blob);
      var a = doc.createElement('a');
      a.href = href;
      a.download = filename;
      a.hidden = true;
      doc.body.appendChild(a);
      a.click();
      if (a.parentNode) { a.parentNode.removeChild(a); }
      if (later) {
        later(function () { urlApi.revokeObjectURL(href); }, 30000);
      } else {
        urlApi.revokeObjectURL(href);
      }
    }

    function build() {
      var files = core.getFiles() || [];
      var usable = usableAssets(assets);
      return fetchAssets(usable.names).then(function (got) {
        return loadZip().then(function (JSZip) {
          var zip = new JSZip();
          var bytesByName = {};
          got.fetched.forEach(function (f) { bytesByName[f.name] = f.bytes; });
          var plan = planDownload(files, got.fetched.map(function (f) { return f.name; }), mode);
          plan.entries.forEach(function (entry) {
            if (entry.kind === 'asset') {
              var bytes = bytesByName[entry.asset];
              if (bytes) { zip.file(entry.path, bytes, { binary: true, compression: 'STORE' }); }
            } else {
              zip.file(entry.path, entry.text || '', { compression: 'DEFLATE', compressionOptions: { level: 6 } });
            }
          });
          return zip.generateAsync({ type: 'blob', mimeType: 'application/zip' }).then(function (blob) {
            save(blob, downloadFileName(btn.getAttribute('data-download-name') || ''));
            var skipped = (got.overCap ? [] : usable.skipped.concat(got.skipped)).concat(plan.refused);
            return downloadNotice({ overCap: got.overCap, skipped: skipped });
          });
        });
      });
    }

    btn.addEventListener('click', function (e) {
      if (e && e.preventDefault) { e.preventDefault(); }
      if (busy) { return; }
      setNotice('', false);
      setBusy(true);
      var run;
      try { run = build(); } catch (err) { run = Promise.reject(err); }
      run.then(function (notice) {
        setNotice(notice, false);
      }, function (err) {
        console.error(err);
        setNotice(FAILED_NOTICE, true);
      }).then(function () { setBusy(false); });
    });
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
}
